/**
 * Online Visa API service.
 *
 * This file wraps all network calls for the online-visa module. It relies only
 * on two cross-module helpers:
 *   - `doPost`          from /src/api/apiUtils
 *   - `getUserAgentId`  from /src/shared/helper-utils/localStorage
 *
 * When copying this module into another client codebase, make sure the two
 * helpers above exist with the same signature (or swap them for the host
 * project's equivalents).
 */

import { doPost } from '../../../api/apiUtils';
import {
  APP_MAIN_URL,
  APP_USER_AUTH,
} from '../../../saas/AppStorageStaticNames';
import {
  getUserAgentId,
  getStringFromLS,
} from '../../../shared/helper-utils/localStorage';
import { ONLINE_VISA_API } from '../constants/onlineVisaConstants';

/**
 * Fetch the list of supported countries (both nationality + destination).
 *
 * @returns {Promise<Array<{ countryName: string, countryCode: string }>>}
 */
export const getOnlineVisaCountriesApi = async () => {
  try {
    const request = {
      opid: ONLINE_VISA_API.OPID,
      actioncode: ONLINE_VISA_API.ACTIONS.GET_COUNTRIES,
      agentid: getUserAgentId(),
      resultformat: 'JSON',
    };
    const response = await doPost(request);
    const data = response?.ApiStatus?.Result || [];
    let list = Array.isArray(data) ? data : [];
    list = list.map((item) => ({
      value: item.country_code || '',
      label: item.country_name || '',
      ...item,
    }));
    return list;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('getOnlineVisaCountriesApi failed:', error);
    return [];
  }
};

/**
 * Convenience helper: pulls traceId + cacheKey from a visa search response so
 * that downstream calls (visa details / required documents) can reuse them.
 */
export const extractVisaContext = (visa, traceId) => ({
  bookingKey: visa?.bookingKey || visa?.visaBookingKey || '',
  providerId: visa?.providerId || 0,
  traceId: traceId || '',
  cacheKey: visa?.cacheKey || '',
});

/**
 * Search available visas for a given nationality + destination.
 *
 * @param {Object} params
 * @param {number} params.adults
 * @param {number} params.children
 * @param {number} params.infants
 * @param {string} params.nationality           e.g. "IN"
 * @param {string} params.clientId              tenant client id, e.g. "tripgainuat"
 * @param {{destinationName: string, destinationCode: string}} params.destination
 * @param {Array<string>} [params.visaTypes]
 * @param {Array<string>} [params.filters]
 * @param {Array<number>} [params.childAges]
 * @param {Array<number>} [params.infantAges]
 * @returns {Promise<Object>}  full response envelope as returned by the API.
 */
export const searchOnlineVisaApi = async (params) => {
  try {
    const searchRequest = {
      adults: 1,
      children: 0,
      infants: 0,
      nationality: 'IN',
      clientId: 'tripgainuat',
      destination: { destinationName: '', destinationCode: '' },
      visaTypes: [],
      filters: [],
      childAges: [],
      infantAges: [],
      ...params,
    };

    const request = {
      opid: ONLINE_VISA_API.OPID,
      actioncode: ONLINE_VISA_API.ACTIONS.VISA_SEARCH,
      agentid: getUserAgentId(),
      searchrequest: JSON.stringify(searchRequest),
    };

    const response = await doPost(request);
    return response;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('searchOnlineVisaApi failed:', error);
    return null;
  }
};

/**
 * Fetch the full detail payload for a single visa (requirements, travel
 * guidelines, FAQs, embassy info, etc.). Corresponds to the FETCHVISADETAILS
 * action code.
 *
 * @param {Object} params
 * @param {string} params.bookingKey      encrypted booking key for the visa
 * @param {number|string} params.providerId
 * @param {string} [params.traceId]
 * @param {string} params.cacheKey
 * @returns {Promise<Object|null>}  the `visaDetail` object (or null on failure).
 */
export const fetchVisaDetailsApi = async ({
  bookingKey,
  providerId,
  traceId,
  cacheKey,
} = {}) => {
  try {
    const request = {
      opid: ONLINE_VISA_API.OPID,
      actioncode: ONLINE_VISA_API.ACTIONS.FETCH_VISA_DETAILS,
      agentid: getUserAgentId(),
      bookingKey: bookingKey || '',
      providerId: providerId != null ? String(providerId) : '',
      traceId: traceId || '',
      cacheKey: cacheKey || '',
      resultformat: 'JSON',
    };
    const response = await doPost(request);
    const result = response?.ApiStatus?.Result || response?.Result || null;
    if (!result) return null;
    return result.visaDetail || result;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('fetchVisaDetailsApi failed:', error);
    return null;
  }
};

/**
 * Fetch the list of required documents for a selected visa. Corresponds to
 * the GETREQUIREDDOCUMENTS action code.
 *
 * @param {Object} params
 * @param {string} params.bookingKey
 * @param {number|string} params.providerId
 * @param {string} [params.traceId]
 * @param {string} params.cacheKey
 * @returns {Promise<Array<{documentName: string, documentContent: string[]}>>}
 */
export const getRequiredDocumentsApi = async ({
  bookingKey,
  providerId,
  traceId,
  cacheKey,
} = {}) => {
  try {
    const request = {
      opid: ONLINE_VISA_API.OPID,
      actioncode: ONLINE_VISA_API.ACTIONS.GET_REQUIRED_DOCUMENTS,
      agentid: getUserAgentId(),
      bookingKey: bookingKey || '',
      providerId: providerId != null ? String(providerId) : '',
      traceId: traceId || '',
      cacheKey: cacheKey || '',
      resultformat: 'JSON',
    };
    const response = await doPost(request);
    const result = response?.ApiStatus?.Result || response?.Result || [];
    return Array.isArray(result) ? result : [];
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('getRequiredDocumentsApi failed:', error);
    return [];
  }
};

/**
 * Upload a document image to the S3 uploader endpoint. Returns the public
 * URL of the uploaded file, or null on failure.
 *
 * @param {File} file           binary file picked by the user
 * @returns {Promise<string|null>}
 */
export const uploadVisaDocumentImageApi = async (file) => {
  if (!file) return null;
  try {
    const agentId = getUserAgentId();
    const sessiontoken = getStringFromLS(APP_USER_AUTH);
    const url = `${getStringFromLS(
      APP_MAIN_URL,
    )}s3fileupload.jsp?agentid=${agentId}&sessiontoken=${sessiontoken}&resultformat=JSON`;
    const form = new FormData();
    form.append('file', file);
    form.append('uploadtype', 'visadocument');
    // eslint-disable-next-line no-undef
    const res = await fetch(url, { method: 'post', body: form });
    // const json = await res.json().catch(() => null);
    const uploaded =
      res?.result ||
      res?.ApiStatus?.Result?.fileUrl ||
      res?.ApiStatus?.Result ||
      res?.Result?.url ||
      res?.url ||
      null;
    return typeof uploaded === 'string' ? uploaded : null;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('uploadVisaDocumentImageApi failed:', error);
    return null;
  }
};

/**
 * Perform visa booking. Wraps the request under a JSON string in the
 * `bookingrequest` form field, matching the contract in the uploaded
 * cURL collection.
 *
 * @param {Object} bookingRequest   full booking payload (see README)
 * @returns {Promise<{ok: boolean, transactionId?: string, message?: string, response?: any}>}
 */
export const performVisaBookingApi = async (bookingRequest) => {
  try {
    const request = {
      opid: ONLINE_VISA_API.OPID,
      actioncode: ONLINE_VISA_API.ACTIONS.PERFORM_VISA_BOOKING,
      agentid: getUserAgentId(),
      bookingrequest: JSON.stringify(bookingRequest || {}),
      resultformat: 'JSON',
    };
    const response = await doPost(request);
    const status = response?.ApiStatus || {};
    const ok = String(status.StatusCode) === '200' || status.Status === 'OK';
    return {
      ok,
      transactionId:
        typeof status.Result === 'string'
          ? status.Result
          : status.Result?.txid || '',
      message: status.Message || '',
      response,
    };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('performVisaBookingApi failed:', error);
    return { ok: false, message: 'Booking request failed', response: null };
  }
};

/**
 * Fetch a full visa application (voucher + invoice payload) by its transaction
 * id. Corresponds to the GETVISAAPPLICATIONBYID action code.
 *
 * @param {string} visaApplicationId   e.g. "VI2104W0VH6W"
 * @returns {Promise<Object|null>} unwrapped Result object (agent/applicants/
 *   documents/application/visaInvoice) or null on failure.
 */
export const getVisaApplicationByIdApi = async (visaApplicationId) => {
  if (!visaApplicationId) return null;
  try {
    const request = {
      opid: ONLINE_VISA_API.OPID,
      actioncode: ONLINE_VISA_API.ACTIONS.GET_VISA_APPLICATION,
      agentid: getUserAgentId(),
      visa_application_id: visaApplicationId,
      resultformat: 'JSON',
    };
    const response = await doPost(request);
    const result = response?.ApiStatus?.Result || response?.Result || null;
    return result || null;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('getVisaApplicationByIdApi failed:', error);
    return null;
  }
};

/**
 * Parse the `farebreakupstring` JSON string on `visaInvoice` (safely) so
 * callers can read the structured pax-wise price list. Returns null on any
 * failure so the caller can fall back to top-level invoice numbers.
 */
export const parseFareBreakup = (visaInvoice) => {
  const raw = visaInvoice?.farebreakupstring;
  if (!raw || typeof raw !== 'string') return null;
  try {
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
};

/**
 * Fetch all visa applications for the agent within a date range.
 *
 * @param {string} fromdate  e.g. "2026-04-20"
 * @param {string} todate    e.g. "2026-04-22"
 * @returns {Promise<Array>}
 */
export const getVisaBookingsListApi = async (fromdate, todate) => {
  try {
    const request = {
      opid: ONLINE_VISA_API.OPID,
      actioncode: ONLINE_VISA_API.ACTIONS.GET_ALL_VISA_APPLICATIONS,
      agentid: getUserAgentId(),
      fromdate,
      todate,
      resultformat: 'JSON',
    };
    const response = await doPost(request);
    const result = response?.ApiStatus?.Result || [];
    return Array.isArray(result) ? result : [];
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('getVisaBookingsListApi failed:', error);
    return [];
  }
};

/**
 * Convenience helper: extracts the visaList array from a search response.
 *
 * Handles both the flat and ApiStatus-wrapped envelopes seen in the collection.
 */
export const extractVisaSearchResult = (response) => {
  if (!response) return { visaList: [], searchCriteria: {}, traceId: '' };

  const result =
    response?.ApiStatus?.Result ||
    response?.Result ||
    (Array.isArray(response?.visaList) ? response : null);

  if (!result) return { visaList: [], searchCriteria: {}, traceId: '' };

  return {
    visaList: Array.isArray(result.visaList) ? result.visaList : [],
    searchCriteria: result.searchCriteria || {},
    traceId: result.traceId || '',
    totalVisas: result.totalVisas || 0,
    pageSize: result.pageSize || 0,
    pageNumber: result.pageNumber || 1,
  };
};
